import React, { memo, useEffect, useRef, useState } from 'react'
import { customFuncsApi } from '../../../api'
import { Empty } from 'antd'
import { ErrorModal } from '../../../components/Modal'
import MainModal from '../../../components/Modal/MainModal'
import { formatNumber } from '../../../utils/formatNumber'
import { useTranslation } from 'react-i18next'
import { get } from 'lodash'
import ClipLoader from 'react-spinners/ClipLoader'

const DASH = '—'

const money = (v) => {
	if (v == null || v === '') return DASH
	const n = Number(v)
	return Number.isNaN(n) ? v : formatNumber(n)
}
// API already returns percent values with the sign, e.g. "22%"
const pct = (v) => (v == null || v === '' ? DASH : v)
const text = (v) => (v == null || v === '' ? DASH : v)

const Label = ({ children, className = '' }) => (
	<span
		className={`block text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-400 ${className}`}
	>
		{children}
	</span>
)

const Hero = ({ label, value, accent }) => (
	<div className="px-6 first:pl-0 last:pr-0">
		<Label>{label}</Label>
		<div
			className={`mt-2 tabular-nums leading-none tracking-tight text-[28px] font-semibold ${accent}`}
		>
			{value}
		</div>
	</div>
)

const LineItem = ({ label, value }) => (
	<div className="flex items-baseline justify-between gap-3 py-[7px] border-b border-dotted border-stone-200 last:border-b-0">
		<span className="text-[11px] uppercase tracking-wider text-stone-500 whitespace-nowrap">
			{label}
		</span>
		<span
			className="text-sm font-medium tabular-nums text-stone-900 truncate"
			title={typeof value === 'string' ? value : ''}
		>
			{value}
		</span>
	</div>
)

const RateCell = ({ label, value, br, bb }) => (
	<div
		className={`flex items-baseline justify-between gap-3 px-3.5 py-3 ${
			br ? 'border-r border-stone-200' : ''
		} ${bb ? 'border-b border-stone-200' : ''}`}
	>
		<span className="text-[10px] uppercase tracking-wider text-stone-500">
			{label}
		</span>
		<span className="text-base font-semibold tabular-nums text-stone-900">
			{value}
		</span>
	</div>
)

const ProductChip = ({ label, value, mono = false }) => (
	<div className="min-w-0">
		<div className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">
			{label}
		</div>
		<div
			className={`text-sm font-medium text-stone-900 truncate ${
				mono ? 'tabular-nums' : ''
			}`}
			title={typeof value === 'string' ? value : ''}
		>
			{value}
		</div>
	</div>
)

const InvoicesModal = ({ getRef = () => {} }) => {
	const { t } = useTranslation()

	const mainModalRef = useRef()
	const errorRef = useRef()

	const [cardCode, setCardCode] = useState('')
	const [cardName, setCardName] = useState('')
	const [row, setRow] = useState(null)
	const [isLoading, setIsLoading] = useState(false)

	const fetchReport = (cc, docEntry) => {
		if (!cc || !docEntry) return
		setIsLoading(true)
		customFuncsApi
			.get('rassrochkaReport', {
				params: {
					CardCode: cc,
					docEntry,
				},
			})
			.then((res) => {
				const list = res.data?.value || []
				setRow(list[0] || null)
			})
			.catch((err) => {
				errorRef.current?.open(
					get(err, 'response.data.message', '') || t('noInfo'),
				)
			})
			.finally(() => {
				setIsLoading(false)
			})
	}

	useEffect(() => {
		const ref = {
			open: ({ cardCode: cc, cardName: cn, docEntry }) => {
				setCardCode(cc || '')
				setCardName(cn || '')
				setRow(null)
				mainModalRef.current?.open()
				fetchReport(cc, docEntry)
			},
			close: () => mainModalRef.current?.close(),
		}
		getRef(ref)
	}, [])

	const renderBody = () => {
		if (isLoading) {
			return (
				<div className="flex justify-center py-16">
					<ClipLoader
						loading={true}
						size={22}
						aria-label="Loading Spinner"
						className={'loader'}
						data-testid="loader"
					/>
				</div>
			)
		}

		if (!row) {
			return (
				<div className="py-10">
					<Empty
						description={t('noInfo')}
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				</div>
			)
		}

		const invoiceNo = text(row['Invoice savdo'])

		return (
			<div className="text-stone-900">
				{/* HEADER BAND */}
				<div className="flex items-end justify-between gap-6 pb-5 border-b border-stone-300">
					<div className="min-w-0">
						<Label>{t('invoices')}</Label>
						<div className="mt-1 flex items-baseline gap-2">
							<span className="text-stone-300 text-2xl leading-none font-semibold">
								№
							</span>
							<span className="text-amber-700 text-[30px] leading-none tabular-nums tracking-tight font-bold">
								{invoiceNo}
							</span>
						</div>
					</div>
					<div className="text-right min-w-0 max-w-[55%]">
						<Label>{t('client')}</Label>
						<div
							className="mt-1 text-base font-semibold text-stone-900 truncate"
							title={cardName || text(row['Klient'])}
						>
							{cardName || text(row['Klient'])}
						</div>
						<div className="text-xs text-stone-500 tabular-nums">
							{cardCode || text(row['Klient raqami'])}
						</div>
					</div>
				</div>

				{/* HERO STRIP */}
				<div className="grid grid-cols-3 divide-x divide-stone-200 py-6">
					<Hero
						label="Сотув нарх"
						value={money(row['Sotuv Narx'])}
						accent="text-stone-900"
					/>
					<Hero
						label="Фойда"
						value={money(row['Foyda'])}
						accent="text-emerald-700"
					/>
					<Hero
						label={t('monthlyPayment')}
						value={money(row["Oylik to'lov"])}
						accent="text-blue-700"
					/>
				</div>

				{/* PRODUCT STRIP */}
				<div className="border-t border-stone-300 pt-4 pb-5">
					<Label className="mb-3">Товар</Label>
					<div className="grid grid-cols-3 gap-6">
						<ProductChip
							label="Tovar nomi"
							value={text(row['Tovar nomi'])}
						/>
						<ProductChip
							label="Seriya"
							value={text(row['Tovar seriyasi'])}
							mono
						/>
						<ProductChip
							label="Ранги"
							value={text(row['Rangi'])}
						/>
					</div>
				</div>

				{/* BREAKDOWN: FINANCE + RATES */}
				<div className="border-t border-stone-300 pt-4 grid grid-cols-5 gap-8">
					<div className="col-span-3">
						<Label className="mb-2">
							Финансовая детализация
						</Label>
						<div className="grid grid-cols-2 gap-x-8">
							<div>
								<LineItem
									label="Таннарх"
									value={money(row['Tannarx'])}
								/>
								<LineItem
									label="Аванс"
									value={money(row['Avans'])}
								/>
								<LineItem
									label="Ойлик фойда"
									value={money(row['Oylik foyda'])}
								/>
							</div>
							<div>
								<LineItem
									label="Қолдиқ + фойда"
									value={money(
										row['Foyda bilan qoldiq'],
									)}
								/>
								<LineItem
									label="Таннарх (авансдан кейин)"
									value={money(
										row['Tannarx avansdan keyin'],
									)}
								/>
							</div>
						</div>
					</div>

					<div className="col-span-2">
						<Label className="mb-2">Ставки</Label>
						<div className="grid grid-cols-2 border border-stone-200">
							<RateCell
								label="Маржа"
								value={pct(row['Marja %'])}
								br
								bb
							/>
							<RateCell
								label="Маржа · год"
								value={pct(row['Marja % yillik'])}
								bb
							/>
							<RateCell
								label="Наценка"
								value={pct(row['Natsenka %'])}
								br
							/>
							<RateCell
								label="Наценка · год"
								value={pct(row['Natsenka % yillik'])}
							/>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<MainModal getRef={(r) => (mainModalRef.current = r)}>
			<div style={{ width: 'min(92vw, 1040px)' }}>
				{/* letterhead accent */}
				<div className="h-px w-full bg-gradient-to-r from-transparent via-amber-600/70 to-transparent mb-5" />
				{renderBody()}
			</div>
			<ErrorModal getRef={(r) => (errorRef.current = r)} />
		</MainModal>
	)
}

export default memo(InvoicesModal)
